import * as Api from '@ViewModels';
import produce from 'immer';
import { useCallback, useMemo } from 'react';

const LOCATION_PROPERTIES = [
	Api.ContactFilterCriteriaProperty.City,
	Api.ContactFilterCriteriaProperty.StateProvince,
	Api.ContactFilterCriteriaProperty.PostalCode,
] as const;

type LocationProperty = (typeof LOCATION_PROPERTIES)[number];

const findLocationCriterion = (
	criteria: Api.IContactFilterCriteria[],
	property: LocationProperty
): Api.IContactFilterCriteria | undefined => {
	return criteria.find(c => c.property === property);
};

export const useLocationFilter = ({
	filter,
	onChangeFilter,
}: {
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}) => {
	const city = useMemo(() => {
		const criterion = findLocationCriterion(filter.filter.criteria, Api.ContactFilterCriteriaProperty.City);
		return criterion?.value ?? '';
	}, [filter.filter.criteria]);

	const state = useMemo(() => {
		const criterion = findLocationCriterion(filter.filter.criteria, Api.ContactFilterCriteriaProperty.StateProvince);
		return criterion?.value ?? '';
	}, [filter.filter.criteria]);

	const zip = useMemo(() => {
		const criterion = findLocationCriterion(filter.filter.criteria, Api.ContactFilterCriteriaProperty.PostalCode);
		return criterion?.value ?? '';
	}, [filter.filter.criteria]);

	const updateLocationFilter = useCallback(
		(property: LocationProperty, value: string) => {
			const nextFilter = produce(filter, draftFilter => {
				const existingIndex = draftFilter.filter.criteria.findIndex(c => c.property === property);

				if (value.trim() === '') {
					// Remove criterion if value is empty
					if (existingIndex > -1) {
						draftFilter.filter.criteria.splice(existingIndex, 1);
					}
				} else {
					// Add or update criterion
					const newCriterion: Api.IContactFilterCriteria = {
						property,
						value: value.trim(),
					};

					if (existingIndex > -1) {
						draftFilter.filter.criteria[existingIndex] = newCriterion;
					} else {
						draftFilter.filter.criteria.push(newCriterion);
					}
				}
			});

			onChangeFilter(nextFilter);
		},
		[filter, onChangeFilter]
	);

	const setCity = useCallback(
		(value: string) => {
			updateLocationFilter(Api.ContactFilterCriteriaProperty.City, value);
		},
		[updateLocationFilter]
	);

	const setState = useCallback(
		(value: string) => {
			updateLocationFilter(Api.ContactFilterCriteriaProperty.StateProvince, value);
		},
		[updateLocationFilter]
	);

	const setZip = useCallback(
		(value: string) => {
			updateLocationFilter(Api.ContactFilterCriteriaProperty.PostalCode, value);
		},
		[updateLocationFilter]
	);

	const clearLocationFilters = useCallback(() => {
		const nextFilter = produce(filter, draftFilter => {
			draftFilter.filter.criteria = draftFilter.filter.criteria.filter(
				c => !LOCATION_PROPERTIES.includes(c.property as LocationProperty)
			);
		});

		onChangeFilter(nextFilter);
	}, [filter, onChangeFilter]);

	const hasLocationFilters = useMemo(() => {
		return city !== '' || state !== '' || zip !== '';
	}, [city, state, zip]);

	return {
		city,
		state,
		zip,
		setCity,
		setState,
		setZip,
		clearLocationFilters,
		hasLocationFilters,
	};
};
